import React, { useEffect, useRef, useState } from 'react';
import { initials, pickAvatarGradient } from './utils';
import RowOriginBadge from '../../components/RowOriginBadge';
import { insertIcdProblemFormed, updateIcdProblemNodeValue } from './process/icdProblemBlot';
import AddProblemDialog from './process/AddProblemDialog';

// Task #213 Phase 1.5 + #223 + #229: header-row Add Problem button opens a
// POP-OUT dialog (AddProblemDialog) instead of triggering Quill's inline
// edit-mode. The inline flow has a recurring 1-letter-at-a-time bug
// (Quill selection observer fights the inline <input>). The dialog
// bypasses Quill entirely during input — clinician types in a regular
// modal, on commit we insert a fully-formed blot in one round-trip.
//
// Task #229 (Leejoe 2026-06-29): the SAME dialog also services edit
// requests now. icdProblemBlot's pencil click dispatches
// 'vmai:edit-icd-problem' with the current displayName+icd10Code+id;
// we listen, open the dialog with those pre-filled, and on commit call
// updateIcdProblemNodeValue instead of insertIcdProblemFormed. Same
// modal flow for create + edit — one input path, no inline-edit-mode
// to fight Quill over.
function AddProblemButton({ editorRef }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode; {id, displayName, icd10Code} = edit
  useEffect(() => {
    const onEditRequest = (e) => {
      const detail = e?.detail || {};
      if (!detail.id) return;
      setEditing({
        id: String(detail.id),
        displayName: String(detail.displayName || ''),
        icd10Code: String(detail.icd10Code || ''),
      });
      setOpen(true);
    };
    window.addEventListener('vmai:edit-icd-problem', onEditRequest);
    return () => window.removeEventListener('vmai:edit-icd-problem', onEditRequest);
  }, []);
  // DoctorPortalBar's "ICD-10" chip + 'I' keyboard shortcut dispatch
  // 'vmai:add-icd-problem' (MedStudentClinicPage's vmai:doctor-action
  // handler forwards it). Open the dialog in create mode — same code
  // path as the visible Add Problem button click.
  useEffect(() => {
    const onAddRequest = () => {
      setEditing(null);
      setOpen(true);
    };
    window.addEventListener('vmai:add-icd-problem', onAddRequest);
    return () => window.removeEventListener('vmai:add-icd-problem', onAddRequest);
  }, []);
  const onAdd = ({ displayName, icd10Code }) => {
    const editor = editorRef?.current?.getEditor?.(0);
    if (!editor) return;
    // Read mode: Quill silently swallows 'user'-sourced inserts, so "Add to
    // note" looked like it did nothing (Leejoe 2026-07-06: "won't input
    // problems anywhere"). Say so instead of no-opping.
    const enabled = typeof editor.isEnabled === 'function'
      ? editor.isEnabled()
      : editor.root?.getAttribute('contenteditable') !== 'false';
    if (!enabled) {
      window.alert('The note is in read mode — click Edit in the header first, then add the problem.');
      return;
    }
    if (editing && editing.id) {
      // Edit-mode commit: mutate the existing blot in place. No new insert.
      updateIcdProblemNodeValue(editing.id, { displayName, icd10Code });
      return;
    }
    insertIcdProblemFormed(editor, { displayName, icd10Code });
  };
  return (
    <>
      <button
        type="button"
        onClick={() => { setEditing(null); setOpen(true); }}
        aria-label="Add ICD-10 problem to Assessment & Plan"
        title="Add a new diagnosis with ICD-10 search to the A&P section"
        className={[
          'text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 whitespace-nowrap shrink-0 text-white shadow-sm',
          'bg-emerald-600 hover:bg-emerald-500',
        ].join(' ')}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add Problem
      </button>
      <AddProblemDialog
        isOpen={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        onAdd={onAdd}
        initialName={editing?.displayName || ''}
        initialCode={editing?.icd10Code || ''}
      />
    </>
  );
}

// Calculate age from DOB. Returns null if DOB is missing.
function calcAge(dob, visitDateStr) {
  if (!dob) return null;
  const birth = new Date(dob);
  const visit = visitDateStr ? new Date(visitDateStr) : new Date();
  if (Number.isNaN(birth.getTime())) return null;
  let age = visit.getFullYear() - birth.getFullYear();
  const m = visit.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && visit.getDate() < birth.getDate())) age--;
  return age;
}

function VersionBadge({ document, versionCount = 0, documentVersionStatus = null, onClick }) {
  // Render even when `document` is null: under MEDSTUDENT_LIVE_COLLAB the
  // client never fetches the doc (the WS owns it), so usePatientDocument
  // hands back `document: null`. The badge is still meaningful because
  // versionCount + documentVersionStatus come from VersioningSystem, not
  // the doc blob.
  const signed = document?.status === 'signed';
  // `document_backups` rows are snapshots; the current (unsaved) doc is one
  // past the latest saved snapshot. Show count when there's history, else V1
  // (the initial draft before any auto-version has fired).
  const v = Math.max(1, versionCount);

  // Prefer the rich label from VersioningSystem (e.g. "v2 · edited",
  // "v3 · behind", "v1 · Stale version") when available, but always keep
  // the SIGNED ✓ marker on top when the document is signed.
  let label;
  if (signed) {
    label = 'SIGNED ✓';
  } else if (documentVersionStatus?.loading) {
    label = '…';
  } else if (documentVersionStatus?.label) {
    // Strip the leading "v<n> · " VersioningSystem already prepends so we
    // don't render "V2 · v2 · edited". Fall back to the bare label if no
    // separator is present (e.g. "v2" alone → "v2" passed through).
    const rest = documentVersionStatus.label.replace(/^v\d+\s*·\s*/i, '').trim();
    label = rest || 'EDITED';
  } else {
    label = 'EDITED';
  }

  let cls;
  if (signed) {
    cls = 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-500/25';
  } else if (documentVersionStatus?.isStaleVersion) {
    cls = 'bg-rose-100 dark:bg-rose-500/15 text-rose-800 dark:text-rose-300 hover:bg-rose-200 dark:hover:bg-rose-500/25';
  } else if (documentVersionStatus?.isOutOfDate) {
    cls = 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-500/25';
  } else if (documentVersionStatus && !documentVersionStatus.loading) {
    cls = 'bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800/60';
  } else {
    cls = 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-500/25';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title="Click to open version history"
      className={`text-[11px] font-mono font-semibold px-2.5 py-1 rounded-md cursor-pointer whitespace-nowrap shrink-0 ${cls}`}
    >
      V{v} · {label}
    </button>
  );
}

// Permission level pill — mirrors EditableDocumentPage's tenant share-role
// badge. `level` is the value returned by resolveTenantRowAccess() in
// utils/shareAccess.js: 'owner' / 'admin' / 'editor' / 'viewer' / 'none'.
// 'none' is hidden so the header doesn't grow a meaningless badge when the
// user has no relationship to the row.
function PermissionBadge({ level }) {
  if (!level || level === 'none') return null;
  const presets = {
    owner: {
      label: 'Owner',
      cls: 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-800 dark:text-indigo-300',
      title: 'You own this document.',
    },
    admin: {
      label: 'Admin',
      cls: 'bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300',
      title: 'Shared with you: full access (edit, share, delete).',
    },
    editor: {
      label: 'Editor',
      cls: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
      title: 'Shared with you: can edit the document.',
    },
    viewer: {
      label: 'Read',
      cls: 'bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200',
      title: 'Shared with you: read-only access.',
    },
  };
  const p = presets[level];
  if (!p) return null;
  return (
    <span
      title={p.title}
      className={`text-[11px] font-semibold px-2.5 py-1 rounded-md whitespace-nowrap shrink-0 ${p.cls}`}
    >
      {p.label}
    </span>
  );
}

// Where this note's .json actually lives in the `files` bucket
// (New/MedADocs/[schema/]year/date/Name-Id.json). Sits with the schema +
// owner chips because it answers the same question they do — "which row /
// which file am I looking at?" — and it's the string you paste into Supabase
// or a bug report, so the whole chip is click-to-copy.
//
// The path is longer than the header can spare, so the visible text is
// ellipsised at the START (direction:rtl on the clipping box, unicode-bidi:
// plaintext on the text so the path itself still reads left-to-right). That
// keeps the informative tail — date + filename — on screen; the full string
// stays in the tooltip and on the clipboard.
function DocPathBadge({ path }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(path);
      setCopied(true);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (insecure origin / permission denied) — the tooltip
         still carries the full path, so this degrades to "look, don't copy". */
    }
  };

  if (!path) return null;

  return (
    <button
      type="button"
      data-testid="doc-path-badge"
      onClick={copy}
      title={`${path}\n(click to copy)`}
      aria-label={`Document file path ${path} — click to copy`}
      className="inline-flex items-center gap-1 font-mono text-[11px] font-medium px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700/60 bg-slate-100 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800/70 shrink"
    >
      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
      </svg>
      <span className="truncate max-w-[14rem] xl:max-w-[26rem]" style={{ direction: 'rtl' }}>
        <span style={{ unicodeBidi: 'plaintext' }}>{path}</span>
      </span>
      {copied && <span className="text-emerald-600 dark:text-emerald-400 shrink-0">✓</span>}
    </button>
  );
}

// Document tools dropdown — mirrors the "Document tools" menu in
// EditableDocumentPage.js (Copy older / Remove duplicates / Reload
// template / Share). Click-to-toggle popover anchored to the trigger,
// closes on outside click or item activation.
function DocumentToolsMenu({
  onCopyOlder,
  onRemoveDuplicates,
  onReloadTemplate,
  onSaveAsTemplate,
  onShare,
  canShare = false,
  disabled = false,
  onOpenAudioManager,
  onSwitchFormat,
  onCombine,
  sectionFormat = 'none',
}) {
  // Label reflects which way the toggle will go. 'none' = no Vitals/ROS section
  // detected (clicking explains there's nothing to switch).
  const switchFormatLabel =
    sectionFormat === 'cards'
      ? 'Switch Vitals/ROS to text'
      : sectionFormat === 'text'
      ? 'Switch back to cards'
      : 'Switch Vitals/ROS format';
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const itemCls =
    'w-full text-left inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={disabled}
        className={[
          'text-xs sm:text-sm font-semibold px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0 text-white shadow-sm shadow-amber-500/25',
          'bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500',
          disabled ? 'opacity-40 cursor-not-allowed' : '',
        ].join(' ')}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="hidden sm:inline">Tools</span>
        <svg
          aria-hidden="true"
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 z-30 w-56 rounded-xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-lg p-1.5"
        >
          {/* Hidden (undefined handler) for read-only patients and roster charts —
              copying from a prior visit is a dated-visit concept. */}
          {onCopyOlder && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onCopyOlder(); }}
              className={`${itemCls} text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/[0.12]`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              Copy from older
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onRemoveDuplicates?.(); }}
            className={`${itemCls} text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/[0.12]`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M16 7V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v3" /></svg>
            Remove duplicates
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onSwitchFormat?.(); }}
            className={`${itemCls} text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-500/[0.12]`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-9L21 7.5m0 0L16.5 3m4.5 4.5H7.5" /></svg>
            {switchFormatLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onOpenAudioManager?.(); }}
            className={`${itemCls} text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-500/[0.12]`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>
            Audio Manager
          </button>
          {onCombine && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onCombine?.(); }}
              className={`${itemCls} text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-500/[0.12]`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Combine documents
            </button>
          )}
          {canShare && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onShare?.(); }}
              className={`${itemCls} text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-500/[0.12]`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
              Share patient
            </button>
          )}
          <div className="my-1 h-px bg-slate-200 dark:bg-slate-800/60" />
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onSaveAsTemplate?.(); }}
            className={`${itemCls} text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/[0.12]`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1-4l-4 4m0 0L8 3m4 4V3" /></svg>
            Save as template
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onReloadTemplate?.(); }}
            className={`${itemCls} text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/[0.12]`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Reload template
          </button>
        </div>
      )}
    </div>
  );
}

// Undo / Redo buttons MOVED — Leejoe 2026-06-29: lives next to the
// voice-typing mic in DocumentToolbar.js now, with the rest of the
// formatting controls. Google Docs convention. See that file for the
// implementation; the underlying Quill `history` module is wired in
// DocumentBody.js (userOnly:true so remote collab syncs don't pollute
// the local undo stack).

// Signature timestamps come off the version row as an ISO string; render them
// in the viewer's locale. Falls back to the raw value if it won't parse, so a
// malformed timestamp degrades to something visible rather than "Invalid Date".
const formatSignedAt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
};

const Btn = ({ children, onClick, kind = 'default', ariaLabel, disabled = false, title }) => {
  // `whitespace-nowrap` + `shrink-0` stops the button label from wrapping
  // its icon onto a second line (visually stacking the icon over the word)
  // when the doc area gets narrow — e.g. when the user drags the right
  // sidebar wider. Buttons keep their natural width regardless of layout
  // pressure; the flex spacer above absorbs the squeeze instead.
  const base = 'text-xs sm:text-sm font-medium px-2 py-1.5 sm:px-3 sm:py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0';
  const styles = {
    default: 'border border-slate-200 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200',
    primary: 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-semibold shadow-sm shadow-emerald-500/25',
    save:    'bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-semibold shadow-sm shadow-blue-500/30 px-3 sm:px-3.5',
    // Action-row colors — each button picks a distinct hue so the cluster
    // reads as a colorful palette rather than a row of grey outlines.
    restore: 'bg-gradient-to-br from-cyan-500 to-sky-600 text-white font-semibold shadow-sm shadow-cyan-500/25',
    audios:  'bg-gradient-to-br from-indigo-500 to-blue-600 text-white font-semibold shadow-sm shadow-indigo-500/25',
    forms:   'bg-gradient-to-br from-violet-500 to-purple-600 text-white font-semibold shadow-sm shadow-violet-500/25',
    meds:    'bg-gradient-to-br from-rose-500 to-pink-600 text-white font-semibold shadow-sm shadow-rose-500/25',
    orders:  'bg-gradient-to-br from-amber-500 to-orange-600 text-white font-semibold shadow-sm shadow-amber-500/25',
    // Signed — deliberately NOT a gradient. Every actionable button above is a
    // saturated gradient, so a flat ringed fill reads as "locked, already
    // done" rather than as one more thing to click.
    signed:  'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold ring-1 ring-slate-300 dark:ring-slate-600 px-3 sm:px-3.5',
  };
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={`${base} ${styles[kind]}${disabled ? ' opacity-70 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

export default function PatientHeaderBar({
  patient,
  document,
  versionCount,
  documentVersionStatus = null,
  permissionLevel = null,
  completed = false,
  readOnly = false,
  // Read-only viewers (e.g. patient role, `isReadOnlyRole(role) === true`)
  // never get the action cluster — Edit / Restore / Add Problem / Forms /
  // Tools / Process / Master Note / Sign are clinician-only. The clinic
  // page still passes `patientReadOnly` here as a single switch so the
  // header can drop the whole cluster in one place instead of relying on
  // per-handler gating (the individual buttons still self-hide on
  // undefined handlers for non-patient read-only contexts, but the patient
  // case is the one where the cluster should never appear at all).
  patientReadOnly = false,
  onToggleReadOnly,
  signedEditing = false,
  onRestore,
  // Opens the Audios modal (ClinicAudiosModal) — every recording on this
  // chart plus the patient's other visits, with player + transcript. Sits
  // next to Restore; undefined hides it, same convention as the rest.
  onAudios,
  onVersionHistory,
  onForms,
  onProcess,
  onMasterNote,
  // True when this user has a Master Note diff saved for this document but not
  // yet applied (Omar 2026-08-17). Badges the Master Note button so a diff
  // saved on another device is discoverable without opening the modal.
  hasSavedDiff = false,
  // Concise mode (Ellen 2026-07-23 — NP stopped using the app: notes too long).
  // Self-service per-clinician toggle. ON => Generate Master Note produces a
  // note bounded by the existing problem list (backend note_style='concise').
  // The parent persists the choice, so it's "click once, stays concise".
  conciseNote = false,
  onToggleConcise,
  onSign,
  signed = false,
  signedBy = '',
  signedAt = null,
  isSigning = false,
  canReveal = false,
  onReveal,
  onCopyOlder,
  onRemoveDuplicates,
  onReloadTemplate,
  onSaveAsTemplate,
  onShare,
  canShare = false,
  onOpenAudioManager,
  onSwitchFormat,
  onCombine,
  // Opens the clinic-side medication management modal. Mirrors the patient's
  // /meds page UI but writes via the doctor-proxy endpoints to the same
  // `patients_with_ehrid.meds` store the patient reads from.
  onOpenClinicMeds = null,
  canOpenClinicMeds = false,
  // Opens the Orders & Followups view for this patient. The rows come from
  // `clinical_action_items` (copilot-extracted on recording save, extracted
  // from the note by patient_orders_scan, or typed by staff in the Compliance
  // tab), and the Paperwork tab reaches the patient's order documents for
  // AI fill and e-signature. Assignment and completion stay in Compliance.
  onOpenClinicOrders = null,
  canOpenClinicOrders = false,
  sectionFormat = 'none',
  unprocessedCount = 0,
  // Task #185 — implicit visit-state detection. True once the clinician
  // has signaled they're actively in the visit (recording started, or
  // first note edit fired). Surfaces as a "🔴 In Visit" badge so the
  // clinician + observer (Ponnezhan during Thursday demo) sees a clear
  // visual marker that the workflow is live — no explicit "Start Visit"
  // gesture required.
  visitInProgress = false,
  // Task #213 Phase 1.5 — Quill editor ref for the Add Problem button
  // (moved from DocumentToolbar to this header row per Leejoe).
  editorRef = null,
  // Row provenance — which schema's table this chart was read from, and who
  // created the row. Sits with the other header badges rather than in its own
  // strip so it costs no vertical space on an already dense clinical page.
  rowSchema = null,
  rowTable = 'notes-c',
  rowCreatorId = null,
  // Storage path of this note's .json (buildDocPath). Rendered as a
  // click-to-copy chip beside the owner name; null when the row has no
  // resolvable path (e.g. a visit row with no date).
  documentPath = null,
}) {
  if (!patient) return null;
  // Pick the same gradient the sidebar card uses for this patient so the
  // avatar color is consistent across the workspace.
  const avatarGradient = pickAvatarGradient(patient, completed);
  // notes-c stores `EHR_ID` not `mrn`; age is computed from `dob`; sex may be in `sex` or `gender`.
  const age = patient.age ?? calcAge(patient.dob, patient.date_);
  const sex = patient.sex || patient.gender || '';
  const mrn = patient.mrn || patient.EHR_ID || '';
  const demoParts = [
    age != null ? `${age} yr` : null,
    sex || null,
    patient.dob ? `DOB ${patient.dob}` : null,
    mrn ? `MRN ${mrn}` : null,
  ].filter(Boolean);
  return (
    <div data-clinic-header className="border-b border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] px-4 py-2 md:px-6 md:py-3 flex flex-wrap items-center gap-x-2 gap-y-2 md:gap-x-4">
      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl ${avatarGradient} flex items-center justify-center text-white text-xs md:text-sm font-bold shrink-0`}>
        {initials(patient.patient_name)}
      </div>
      {/* Patient info column. `min-w-min` (CSS min-content) lets the
          column shrink down to the WIDEST UNBREAKABLE WORD — so
          "Patient, Test" can wrap to two lines, the demo line can split
          across multiple, but the column never gets so narrow that a
          single word breaks character-by-character. That's the user-
          visible "as compact as a per-word layout" limit; once the
          column hits min-content and the header still overflows, the
          drag cap above kicks in. */}
      <div className="min-w-min flex-shrink">
        <div className="font-bold text-sm md:text-base leading-tight text-slate-900 dark:text-slate-100">{patient.patient_name}</div>
        <div className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {demoParts.join(' · ')}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-2">
        {visitInProgress && (
          <span
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300 inline-flex items-center gap-1.5"
            aria-label="Visit in progress"
            title="Visit in progress — recording or editing"
          >
            <span
              className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"
              aria-hidden="true"
            />
            In Visit
          </span>
        )}
        {(patient.tags || []).map(t => (
          <span key={t} className="text-[10px] md:text-[11px] font-semibold px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300">{t}</span>
        ))}
        <VersionBadge
          document={document}
          versionCount={versionCount}
          documentVersionStatus={documentVersionStatus}
          onClick={onVersionHistory || onRestore}
        />
        <PermissionBadge level={permissionLevel} />
        {/* Hidden from the patient portal: schema names are internal tenant
            plumbing, and which clinician owns the row is not the patient's
            orientation cue. Every clinical viewer sees it. */}
        {!patientReadOnly && (
          <RowOriginBadge
            schema={rowSchema}
            table={rowTable}
            userId={rowCreatorId}
          />
        )}
        {/* Same reasoning as the badge above — a storage path is internal
            plumbing, so the patient portal never sees it. */}
        {!patientReadOnly && documentPath && <DocPathBadge path={documentPath} />}
      </div>
      <div className="flex-1 min-w-0" />
      {/* Action group. When the doc area is too narrow to fit it on the same
          line as the patient info, `flex-wrap` on the header drops the whole
          cluster to a second row. If the cluster itself is still too wide for
          the row, `flex-wrap` here lets its buttons (Edit / Restore / Forms /
          tools) flow onto further rows — right-aligned via justify-end — rather
          than overflowing into the right sidebar. Hidden entirely for patient
          role (read-only viewers): patient viewers see the header for
          orientation (name + version badge) but no actionable controls. */}
      {!patientReadOnly && (
      <div className="flex flex-wrap items-center justify-end gap-1.5 md:gap-2 ml-auto">
        {/* Read / Edit toggle. Document defaults to read mode so a student
            can scan / copy without risk of accidental edits; clicking flips
            into edit mode. Visual state mirrors the destination: when in
            read mode the button is "✎ Edit" (the action), when in edit mode
            it's "👁 Reading" with an amber tint to signal write-mode is on.
            Patient role (read-only viewers) never get this button: the
            clinic page passes `onToggleReadOnly=undefined` for
            `patientReadOnly`, mirroring the existing onForms / onSign
            pattern. */}
        {onToggleReadOnly && (
        <button
          type="button"
          onClick={onToggleReadOnly}
          aria-pressed={!readOnly}
          aria-label={readOnly ? 'Switch to edit mode' : (signedEditing ? 'Finish editing and save a tracked version' : 'Switch to read mode')}
          title={readOnly ? 'Currently read-only — click to edit' : (signedEditing ? 'Finish editing — saves your changes as a tracked version and re-locks the note' : 'Currently editing — click to lock')}
          className={
            readOnly
              ? [
                  // Loud "doc is locked — click to unlock" CTA. Fuchsia/pink
                  // gradient is intentionally distinct from the rest of the
                  // action cluster (cyan / violet / amber / emerald / blue)
                  // so the eye snaps to it the moment the doc opens in read
                  // mode. Larger label + pulse reinforces the affordance.
                  'text-xs sm:text-sm md:text-base font-semibold px-2.5 py-1.5 sm:px-4 sm:py-1.5 rounded-lg flex items-center gap-1.5 whitespace-nowrap shrink-0 text-white',
                  'bg-gradient-to-br from-fuchsia-500 to-pink-600 hover:from-fuchsia-400 hover:to-pink-500',
                  'shadow-md shadow-fuchsia-500/40 ring-1 ring-fuchsia-300/50 dark:ring-fuchsia-400/30 animate-pulse',
                ].join(' ')
              : signedEditing
                ? [
                    // Post-sign edit session: this toggle IS the "finish & save a
                    // tracked version" action, so it reads as a commit (emerald)
                    // rather than a passive "you're editing" (amber) state.
                    'text-xs sm:text-sm font-semibold px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg flex items-center gap-1.5 border whitespace-nowrap shrink-0',
                    'border-emerald-400 dark:border-emerald-500/50 bg-emerald-50 dark:bg-emerald-500/[0.12] text-emerald-800 dark:text-emerald-200',
                  ].join(' ')
                : [
                    'text-xs sm:text-sm font-medium px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg flex items-center gap-1.5 border whitespace-nowrap shrink-0',
                    'border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/[0.08] text-amber-800 dark:text-amber-200',
                  ].join(' ')
          }
        >
          {readOnly ? (
            <>
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </>
          ) : signedEditing ? (
            <>
              <span aria-hidden="true">✓</span> <span className="hidden sm:inline">Done editing</span>
            </>
          ) : (
            <>
              <span>🔓</span> <span className="hidden sm:inline">Editing</span>
            </>
          )}
        </button>
        )}
        {/* Restore is gated on `onRestore` so a stray missing handler (e.g.
            a future non-patient read-only viewer) doesn't render a button
            that would no-op. The `patientReadOnly` cluster wrap is the
            primary gate for the patient role. */}
        {onRestore && <Btn onClick={onRestore} ariaLabel="Restore" kind="restore" disabled={readOnly} title={readOnly ? 'Switch to edit mode to restore' : undefined}>↻ Restore</Btn>}
        {/* Audios — view-only, so unlike Restore it stays live in read mode:
            listening back to a recording is exactly what you do while the
            note is locked. */}
        {onAudios && (
          <Btn
            onClick={onAudios}
            ariaLabel="Audios"
            kind="audios"
            title="Play this patient's recordings and read their transcripts"
          >
            🎧 Audios
          </Btn>
        )}
        {/* Reset to Seed moved to DoctorPortalBar top white bar (Task #227) —
            test-fixture affordance now visually separated from real-patient
            action row. Hidden for patient role via the `patientReadOnly`
            cluster wrap above. */}
        <AddProblemButton editorRef={editorRef} />
        {onForms && <Btn onClick={onForms} ariaLabel="Forms" kind="forms">📋 Forms</Btn>}
        {onOpenClinicMeds && (
          <Btn
            onClick={onOpenClinicMeds}
            ariaLabel="Open patient medications"
            kind="meds"
            disabled={!canOpenClinicMeds}
            title="Open the patient's medication list — same view as /meds on the patient portal, with manual add/remove plus 'Add meds from latest visit'"
          >
            💊 Meds
          </Btn>
        )}
        {/* Orders — physician-only (roles.json clinicPage.ordersButton, the
            same three roles as Sign). Reading the list is safe while the note
            is locked, but this is also the entry point for filling and
            e-signing order paperwork, so it is gated like Sign rather than
            like the read-only viewers. The caller passes `undefined` for
            anyone else, which removes the button instead of disabling it. */}
        {onOpenClinicOrders && (
          <Btn
            onClick={onOpenClinicOrders}
            ariaLabel="Open patient orders and followups"
            kind="orders"
            disabled={!canOpenClinicOrders}
            title="See this patient's orders and followups — extracted from visit recordings or entered by staff in the Compliance tab"
          >
            📋 Orders
          </Btn>
        )}
        {/* Tools menu gated on at least one handler being defined so a
            viewer with no tools (e.g. patient role via `patientReadOnly`
            cluster wrap) gets no dropdown at all. Existing handler-gating
            inside the menu still hides individual items for read-only
            viewers. */}
        {(onCopyOlder || onRemoveDuplicates || onReloadTemplate || onSaveAsTemplate || onShare || onOpenAudioManager || onSwitchFormat || onCombine) && (
          <DocumentToolsMenu
            onCopyOlder={onCopyOlder}
            onRemoveDuplicates={onRemoveDuplicates}
            onReloadTemplate={onReloadTemplate}
            onSaveAsTemplate={onSaveAsTemplate}
            onShare={onShare}
            canShare={canShare}
            onOpenAudioManager={onOpenAudioManager}
            onSwitchFormat={onSwitchFormat}
            onCombine={onCombine}
            sectionFormat={sectionFormat}
          />
        )}
        <span className="relative inline-flex shrink-0">
          {onProcess && <Btn onClick={onProcess} ariaLabel="Process" kind="primary">⚡ Process</Btn>}
          {unprocessedCount > 0 && onProcess && (
            <span
              aria-label={`${unprocessedCount} unprocessed sources`}
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center"
            >
              {unprocessedCount}
            </span>
          )}
        </span>
        {/* Master Note — opens ProcessModal directly (whole-note rewrite via
            /api/master-note/). Separate from the Process button because the
            Process button was rewired to Diego's Parked Review flow on
            prd0701, orphaning our Master Note frontend from prd0630. This
            button restores a trigger for ProcessModal → Master Note without
            disturbing Diego's flow. */}
        {onMasterNote && (
          <span className="relative inline-flex shrink-0">
            <button
              type="button"
              onClick={onMasterNote}
              aria-label="Generate Master Note — whole-note rewrite (physician)"
              title="Generate Master Note — the physician's whole-note rewrite from the visit (vs Generate Diff, the intake tool)"
              className={[
                'text-xs sm:text-sm font-semibold px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg flex items-center gap-1.5 whitespace-nowrap shrink-0 text-white',
                'bg-gradient-to-br from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500',
                'shadow-md shadow-violet-500/40 ring-1 ring-violet-300/50 dark:ring-violet-400/30',
              ].join(' ')}
            >
              ✨ <span className="hidden sm:inline">Generate Master Note</span>
            </button>
            {/* A Master Note this user saved for this chart — quite possibly on
                another device — is sitting in storage unapplied. Without this
                the only way to discover it is to open Process and look, which
                defeats the point of saving it. */}
            {hasSavedDiff && (
              <span
                aria-label="Saved Master Note diff waiting"
                title="You have a saved Master Note diff for this note — open Generate Master Note to review it"
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-slate-900"
              >
                💾
              </span>
            )}
          </span>
        )}
        {onMasterNote && onToggleConcise && (
          <button
            type="button"
            onClick={onToggleConcise}
            aria-pressed={conciseNote}
            title={conciseNote
              ? 'Concise notes ON — every note you generate stays focused on the problems already in the chart. Click to turn off.'
              : 'Concise notes OFF — click once to make every note you generate concise (focused only on the problems already in the chart).'}
            className={[
              'text-xs sm:text-sm font-semibold px-2.5 py-1.5 rounded-lg flex items-center gap-1 whitespace-nowrap shrink-0 border transition-colors',
              conciseNote
                ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/40'
                : 'bg-slate-100 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600/50 hover:bg-slate-200 dark:hover:bg-slate-700/60',
            ].join(' ')}
          >
            {conciseNote ? '◉' : '○'} <span className="hidden sm:inline">Concise</span>
          </button>
        )}
        {/* Once signed, the button becomes a locked status indicator rather
            than an action. Signed state is DB-backed (a version row carrying
            the SIGNED_VERSION_PREFIX marker), so it survives reload/logout and
            shows the same for every viewer — including the original signer's
            name and timestamp on hover. */}
        {/* The entire sign/signed cluster is clinician-only. `onSign` is
            provided only for clinical roles (undefined for the non-clinical
            tenant roles + patients), so its absence hides BOTH the actionable
            Sign & Save button AND the signed status badge. */}
        {!onSign ? null : signed ? (
          canReveal ? (
            <Btn
              kind="signed"
              onClick={onReveal}
              ariaLabel={`Signed${signedBy ? ` by ${signedBy}` : ''}${signedAt ? ` on ${formatSignedAt(signedAt)}` : ''} — click to view password`}
              title={`Signed${signedBy ? ` by ${signedBy}` : ''}${signedAt ? ` on ${formatSignedAt(signedAt)}` : ''} — click to view password`}
            >
              <span className="text-emerald-600 dark:text-emerald-400" aria-hidden="true">✓</span> Signed
            </Btn>
          ) : (
            <Btn
              kind="signed"
              disabled
              ariaLabel={`Signed${signedBy ? ` by ${signedBy}` : ''}${signedAt ? ` on ${formatSignedAt(signedAt)}` : ''}`}
              title={`Signed${signedBy ? ` by ${signedBy}` : ''}${signedAt ? ` on ${formatSignedAt(signedAt)}` : ''}`}
            >
              <span className="text-emerald-600 dark:text-emerald-400" aria-hidden="true">✓</span> Signed
            </Btn>
          )
        ) : (
          <Btn
            onClick={onSign}
            disabled={isSigning}
            ariaLabel="Sign and Save"
            kind="save"
          >
            {isSigning ? '… Signing' : '✓ Sign & Save'}
          </Btn>
        )}
      </div>
      )}
    </div>
  );
}
